import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setLoading } from "../../../../actions";
import { scrollToErrorTarget } from "./errorNavigation";
import { COLOR_SCHEMES } from "../../../../styles/colors/colors";

const { BLUE, PURPLE, PURPLE_SECONDARY, BUTTON_DEACTIVATED } =
  COLOR_SCHEMES.PRIMARY_GRADIENT;
// Same brand gradient used by the section headers / gradient buttons.
const GRADIENT = `linear-gradient(45deg, ${BLUE}, ${PURPLE_SECONDARY}, ${PURPLE})`;

const buttonStyle = (disabled, extra) => ({
  flex: 1,
  padding: "8px 10px",
  border: "none",
  borderRadius: "8px",
  background: disabled ? BUTTON_DEACTIVATED : GRADIENT,
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "13px",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.85 : 1,
  ...extra,
});

const resolveTargetEl = (target) => {
  if (!target) return null;
  const variants = [target];
  if (target.indexOf("[") > -1) {
    variants.push(target.replace(/\[(\d+)\]/g, ".$1"));
  }
  for (const n of variants) {
    const el = document.getElementsByName(n)[0] || document.getElementById(n);
    if (el) return el;
  }
  return null;
};

// Lightweight "looks addressed" check — does the target field have a value now?
// Not validation; full validation still happens only at Proceed.
const isTargetFilled = (target) => {
  if (!target) return false;
  if (target === "opt-group") {
    const radios = document.getElementsByName("opt-group");
    for (let i = 0; i < radios.length; i += 1) {
      if (radios[i].checked) return true;
    }
    return false;
  }
  let el = resolveTargetEl(target);
  if (!el) return false;
  const tag = el.tagName;
  if (tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") {
    el = el.querySelector("input, select, textarea") || el;
  }
  return !!(el && el.value && String(el.value).trim() !== "");
};

// Floating "fix issues" navigator.
// Moving to an error on a DIFFERENT page runs the page's real "Save and
// Continue" (POST) first, then navigates. The company-enrolment save saga
// writes the saved company data straight back into the store, so the next page
// shows the authoritative record without a separate reload. Moving within the
// SAME page just scrolls (no unmount, nothing to persist yet).
const ErrorNavigator = () => {
  const dispatch = useDispatch();
  const errorNav = useSelector(
    (store) => store.generalReducer && store.generalReducer.errorNav
  );
  const ce =
    useSelector(
      (store) => store.companyReducer && store.companyReducer.companyEnrolment
    ) || {};
  // The company-enrolment save flags aren't a reliable true->false edge, but the
  // save saga toggles the global loading flag around every POST, so that is the
  // signal we watch. saveCompanyEnrolmentDataFailed tells us the outcome.
  const saving = useSelector(
    (store) => store.generalReducer && store.generalReducer.loading
  );
  const saveFailed = ce.saveCompanyEnrolmentDataFailed;

  const [addressed, setAddressed] = useState({});
  const pendingRef = useRef(null); // navigation target awaiting POST
  const phaseRef = useRef(null); // 'saving' | null
  const prevSavingRef = useRef(false);
  const fallbackRef = useRef(null);

  const clearPending = () => {
    pendingRef.current = null;
    phaseRef.current = null;
    if (fallbackRef.current) {
      clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
  };

  const runPending = () => {
    const pending = pendingRef.current;
    clearPending();
    if (!pending) return;
    // Defer so our view change lands AFTER the save saga's own setLoading (it
    // resets the view to the saved record's step). A macrotask runs after the
    // saga's synchronous dispatches, so our target view wins deterministically.
    setTimeout(() => {
      if (pending.type === "summary") {
        dispatch(setLoading({ errorNav: null, view: "STEP_SIX" }));
        scrollToErrorTarget("err-summary-proceed", false);
        return;
      }
      const next = pending.errors[pending.index];
      dispatch(
        setLoading({
          errorNav: { errors: pending.errors, index: pending.index },
          view: next.view,
        })
      );
      scrollToErrorTarget(next.target);
    }, 80);
  };

  // Reset addressed map when a session ends.
  useEffect(() => {
    if (!errorNav) setAddressed({});
  }, [errorNav]);

  // Self-heal stale snapshots: the Government Photo ID fields are a prepay-only
  // (FINTRAC) requirement and aren't even rendered on a postpayment form. If the
  // saved program is explicitly NOT prepay, prune those issues from the list so
  // the navigator never steers to a field that doesn't exist (which otherwise
  // dead-ends at the top of the page).
  useEffect(() => {
    if (!errorNav || !errorNav.errors || errorNav.errors.length === 0) return;
    const paymentType =
      ce.companyData &&
      ce.companyData.business &&
      ce.companyData.business.programDefinedVariables &&
      ce.companyData.business.programDefinedVariables.contractTerm &&
      ce.companyData.business.programDefinedVariables.contractTerm
        .programPaymentType;
    // Only prune when we can confirm the program is explicitly not prepay.
    if (!paymentType || paymentType === "prepaymentmastercard") return;
    const PREPAY_ONLY_KEYS = [
      "Government Photo ID Number",
      "Government photo ID Expiry Date",
    ];
    const cleaned = errorNav.errors.filter(
      (e) => !PREPAY_ONLY_KEYS.includes(e.key)
    );
    if (cleaned.length === errorNav.errors.length) return;
    const current = errorNav.errors[errorNav.index];
    let newIndex = cleaned.findIndex((e) => e.key === current.key);
    if (newIndex < 0) {
      newIndex = Math.min(errorNav.index, cleaned.length - 1);
    }
    dispatch(
      setLoading({
        errorNav: cleaned.length
          ? { errors: cleaned, index: Math.max(0, newIndex) }
          : null,
      })
    );
  }, [errorNav, ce.companyData, dispatch]);

  // Poll the current issue's field; mark addressed once it has a value.
  useEffect(() => {
    if (!errorNav || !errorNav.errors || errorNav.errors.length === 0) {
      return undefined;
    }
    const check = () => {
      const cur = errorNav.errors[errorNav.index];
      if (cur && !addressed[cur.key] && isTargetFilled(cur.target)) {
        setAddressed((prev) => ({ ...prev, [cur.key]: true }));
      }
    };
    check();
    const id = setInterval(check, 700);
    return () => clearInterval(id);
  }, [errorNav, addressed]);

  // POST finished -> navigate to the target error. The save saga refreshes the
  // stored company data itself, so there is no separate reload step.
  useEffect(() => {
    const was = prevSavingRef.current;
    prevSavingRef.current = saving;
    if (was && !saving && pendingRef.current && phaseRef.current === "saving") {
      if (saveFailed) {
        clearPending(); // step shows its own alert; stay put
        return;
      }
      runPending();
    }
  }, [saving, saveFailed]);

  if (!errorNav || !errorNav.errors || errorNav.errors.length === 0) {
    return null;
  }

  const { errors, index } = errorNav;
  const current = errors[index] || errors[0];
  const currentView = current.view;
  const atStart = index <= 0;
  const atEnd = index >= errors.length - 1;
  const allAddressed = errors.every((e) => addressed[e.key]);
  const busy = saving || !!pendingRef.current;

  // Persist current page (POST) then navigate.
  const startSave = (target) => {
    if (pendingRef.current) return;
    const saveBtn = document.querySelector('[data-testid="saveAndContinue"]');
    pendingRef.current = target;
    // Only drive the save flow when the button can actually be clicked. While
    // fixing issues the current page is usually incomplete, so "Save and
    // Continue" is disabled - clicking it is a no-op and the navigation would
    // stall (until the 10s fallback). In that case just navigate to the target
    // issue directly, exactly like jumping from the summary.
    if (saveBtn && !saveBtn.disabled) {
      phaseRef.current = "saving";
      fallbackRef.current = setTimeout(() => {
        if (pendingRef.current) runPending();
      }, 10000);
      saveBtn.click();
    } else {
      runPending(); // no (enabled) save button -> just navigate
    }
  };

  const goTo = (newIndex) => {
    if (newIndex < 0 || newIndex >= errors.length || busy) return;
    const target = errors[newIndex];
    if (target.view === currentView) {
      // Same page: nothing unmounts, nothing to persist yet -> just scroll.
      dispatch(setLoading({ errorNav: { errors, index: newIndex } }));
      scrollToErrorTarget(target.target);
    } else {
      startSave({ type: "goto", errors, index: newIndex });
    }
  };

  const goToSummary = () => {
    if (!busy) startSave({ type: "summary" });
  };


  const statusText = saving
    ? "Saving…"
    : `Issue ${index + 1} of ${errors.length}`;

  return (
    <div
      style={{
        position: "fixed",
        // Anchor next to the centered ~600px form (half-width 300px + ~20px gap)
        // instead of the viewport edge, so the popup sits close to the form on
        // wide monitors. Clamp keeps it ≥24px from the right edge on narrow screens.
        left: "min(calc(50% + 320px), calc(100% - 264px))",
        top: "38%",
        width: "240px",
        backgroundColor: "#ffffff",
        border: `1px solid ${PURPLE_SECONDARY}`,
        borderRadius: "12px",
        boxShadow: "0 8px 24px rgba(161, 49, 199, 0.18)",
        padding: "16px",
        zIndex: 1000,
        fontFamily: "Montserrat",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <span style={{ fontWeight: 700, color: PURPLE_SECONDARY, fontSize: "13px" }}>
          Fixing issues
        </span>
        <span
          onClick={goToSummary}
          title="Ready to Submit"
          style={{
            cursor: "pointer",
            color: "#999999",
            fontSize: "16px",
            lineHeight: 1,
          }}
        >
          ✕
        </span>
      </div>

      <div style={{ fontSize: "12px", color: "#666666", marginBottom: "8px" }}>
        {statusText}
      </div>

      <div style={{ fontWeight: 700, color: "#333333", fontSize: "13px" }}>
        {current.key}
      </div>
      <div style={{ fontSize: "12px", color: "#555555", marginBottom: "14px" }}>
        {current.message}
      </div>

      <div style={{ display: "flex", gap: "8px" }}>
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          disabled={atStart || busy}
          style={buttonStyle(atStart || busy)}
        >
          ‹ Prev
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          disabled={atEnd || busy}
          style={buttonStyle(atEnd || busy)}
        >
          Next ›
        </button>
      </div>

      {allAddressed ? (
        <button
          type="button"
          onClick={goToSummary}
          disabled={busy}
          style={buttonStyle(busy, {
            flex: "none",
            width: "100%",
            marginTop: "12px",
          })}
        >
          Ready to Submit ›
        </button>
      ) : (
        <div
          onClick={goToSummary}
          style={{
            marginTop: "12px",
            textAlign: "center",
            fontSize: "12px",
            color: PURPLE_SECONDARY,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Ready to Submit
        </div>
      )}
    </div>
  );
};

export default ErrorNavigator;
